import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Inbox, Mail, StickyNote, Send, Sparkles, Loader2, Copy } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import Loading from "@/components/dashboard/Loading";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Won"];
const statusColor = {
  New: "border-[#4F8CFF]/30 bg-[#4F8CFF]/10 text-[#8fb4ff]",
  Contacted: "border-amber-500/30 bg-amber-500/10 text-amber-400",
  Won: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
};

function toCSV(rows) {
  const headers = ["Name", "Email", "Message", "Notes", "Status", "Source", "Submitted"];
  const keys = ["name", "email", "message", "notes", "status", "source", "created_at"];
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.join(",")];
  rows.forEach((r) => lines.push(keys.map((k) => escape(r[k])).join(",")));
  return lines.join("\n");
}

function followUpHref(r) {
  const subject = encodeURIComponent("Your Vvvoraa enquiry — let's build your AI agent");
  const body = encodeURIComponent(`Hi${r.name ? " " + r.name : ""},\n\nThanks for reaching out via the Vvvoraa AI chat. We'd love to learn more about what you need and design the right AI agent for you.\n\nBest,\nThe Vvvoraa team`);
  return `mailto:${r.email}?subject=${subject}&body=${body}`;
}

export default function LeadsInbox() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["leads"],
    queryFn: async () => (await api.get("/leads")).data,
    retry: false,
  });

  const [notesFor, setNotesFor] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const [draftFor, setDraftFor] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);

  const openDraft = async (lead) => {
    setDraftFor(lead);
    setDraftText("");
    setDraftLoading(true);
    try {
      const { data } = await api.post(`/ai/leads/${lead.id}/draft`);
      setDraftText(data.draft);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate a draft.");
      setDraftFor(null);
    } finally {
      setDraftLoading(false);
    }
  };

  const copyDraft = () => { navigator.clipboard.writeText(draftText); toast.success("Draft copied"); };
  const emailDraft = () => {
    const subject = encodeURIComponent("Following up from Vvvoraa");
    window.location.href = `mailto:${draftFor.email}?subject=${subject}&body=${encodeURIComponent(draftText)}`;
  };

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/leads/${lead.id}/status`, { status });
      await qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success(`${lead.email} marked as ${status}`);
    } catch { toast.error("Could not update status"); }
  };

  const openNotes = (lead) => { setNotesFor(lead); setNotesText(lead.notes || ""); };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/leads/${notesFor.id}/notes`, { notes: notesText });
      await qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Notes saved");
      setNotesFor(null);
    } catch { toast.error("Could not save notes"); }
    finally { setSavingNotes(false); }
  };

  const download = () => {
    const csv = toCSV(data || []);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vvvoraa-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  if (isLoading) {
    return (<div><PageHeader title="Leads" subtitle="Enquiries captured from the AI chat widget." showDemo={false} /><Loading /></div>);
  }

  if (error) {
    return (
      <div>
        <PageHeader title="Leads" subtitle="Enquiries captured from the AI chat widget." showDemo={false} />
        <Panel className="p-12 text-center">
          <p className="text-white font-medium">Admin access required</p>
          <p className="mt-1 text-sm text-slate-400">Only the Vvvoraa admin account can view captured leads.</p>
        </Panel>
      </div>
    );
  }

  const rows = data || [];

  const StatusSelect = ({ r }) => (
    <Select value={r.status || "New"} onValueChange={(v) => setStatus(r, v)}>
      <SelectTrigger className={`h-8 w-[120px] rounded-full border text-xs ${statusColor[r.status || "New"]}`} data-testid={`lead-status-${r.id}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-[#0A1119] border-white/10 text-white">
        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const RowActions = ({ r }) => (
    <div className="flex items-center gap-1">
      <button onClick={() => openDraft(r)} disabled={!r.email} className="p-1.5 rounded-lg text-slate-400 hover:text-[#7C5CFF] hover:bg-[#7C5CFF]/10 transition-colors disabled:opacity-40" title="AI draft reply" data-testid={`lead-draft-${r.id}`}>
        <Sparkles className="h-3.5 w-3.5" />
      </button>
      <button onClick={() => openNotes(r)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors relative" title="Notes" data-testid={`lead-notes-${r.id}`}>
        <StickyNote className="h-3.5 w-3.5" />
        {r.notes ? <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-[#4F8CFF]" /> : null}
      </button>
      <a href={followUpHref(r)} className="p-1.5 rounded-lg text-slate-400 hover:text-[#4F8CFF] hover:bg-[#4F8CFF]/10 transition-colors" title="Follow up by email" data-testid={`lead-followup-${r.id}`}>
        <Send className="h-3.5 w-3.5" />
      </a>
    </div>
  );

  return (
    <div>
      <PageHeader title="Leads" subtitle={`${rows.length} lead${rows.length === 1 ? "" : "s"} from the AI chat widget.`} showDemo={false}>
        <Button onClick={download} disabled={!rows.length} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full gap-1.5" data-testid="leads-export-btn">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </PageHeader>

      {rows.length === 0 ? (
        <Panel className="p-12 text-center" data-testid="leads-empty">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#4F8CFF]/12 text-[#4F8CFF]"><Inbox className="h-7 w-7" /></div>
          <p className="mt-4 text-white font-medium">No leads yet</p>
          <p className="mt-1 text-sm text-slate-400">Emails captured in the chat widget will appear here.</p>
        </Panel>
      ) : (
        <>
          <Panel className="hidden md:block overflow-hidden" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/8 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3.5 font-medium">Name</th>
                  <th className="px-5 py-3.5 font-medium">Email</th>
                  <th className="px-5 py-3.5 font-medium">Context</th>
                  <th className="px-5 py-3.5 font-medium">Source</th>
                  <th className="px-5 py-3.5 font-medium">Status</th>
                  <th className="px-5 py-3.5 font-medium">Actions</th>
                  <th className="px-5 py-3.5 font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors" data-testid={`leads-row-${r.id}`}>
                    <td className="px-5 py-4 text-white">{r.name || "—"}</td>
                    <td className="px-5 py-4 text-slate-300">{r.email}</td>
                    <td className="px-5 py-4 text-slate-400 max-w-xs truncate">{r.message || "—"}</td>
                    <td className="px-5 py-4"><span className="text-[10px] font-mono uppercase tracking-wider rounded-full border border-white/12 px-2 py-0.5 text-slate-400">{r.source}</span></td>
                    <td className="px-5 py-4"><StatusSelect r={r} /></td>
                    <td className="px-5 py-4"><RowActions r={r} /></td>
                    <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <div className="md:hidden space-y-3">
            {rows.map((r) => (
              <Panel key={r.id} className="p-4" data-testid={`leads-card-${r.id}`}>
                <p className="text-sm font-semibold text-white">{r.name || "Anonymous"}</p>
                <p className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1.5"><Mail className="h-3 w-3" /> {r.email}</p>
                {r.message ? <p className="mt-2 text-xs text-slate-500">{r.message}</p> : null}
                <div className="mt-3 flex items-center justify-between gap-2">
                  <p className="text-[11px] text-slate-500">{r.source}</p>
                  <div className="flex items-center gap-2">
                    <RowActions r={r} />
                    <StatusSelect r={r} />
                  </div>
                </div>
              </Panel>
            ))}
          </div>
        </>
      )}

      {/* Notes dialog */}
      <Dialog open={!!notesFor} onOpenChange={(o) => !o && setNotesFor(null)}>
        <DialogContent className="bg-[#0A1119] border-white/10 text-white" data-testid="lead-notes-dialog">
          <DialogHeader><DialogTitle>Notes — {notesFor?.email}</DialogTitle></DialogHeader>
          <Textarea
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            placeholder="Call outcome, next steps, budget mentioned…"
            rows={5}
            className="bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500"
            data-testid="lead-notes-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesFor(null)} className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full">Cancel</Button>
            <Button onClick={saveNotes} disabled={savingNotes} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full" data-testid="lead-notes-save">{savingNotes ? "Saving…" : "Save notes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI draft reply dialog */}
      <Dialog open={!!draftFor} onOpenChange={(o) => !o && setDraftFor(null)}>
        <DialogContent className="bg-[#0A1119] border-white/10 text-white max-w-lg" data-testid="lead-draft-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#7C5CFF]" /> AI reply draft — {draftFor?.email}</DialogTitle></DialogHeader>
          {draftLoading ? (
            <div className="py-12 flex flex-col items-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin text-[#7C5CFF]" /><p className="mt-3 text-sm">Drafting a personalized reply…</p></div>
          ) : (
            <Textarea value={draftText} onChange={(e) => setDraftText(e.target.value)} rows={10} className="bg-white/[0.03] border-white/12 text-white" data-testid="lead-draft-text" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={copyDraft} disabled={draftLoading || !draftText} className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full gap-1.5" data-testid="lead-draft-copy"><Copy className="h-3.5 w-3.5" /> Copy</Button>
            <Button onClick={emailDraft} disabled={draftLoading || !draftText} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full gap-1.5" data-testid="lead-draft-email"><Send className="h-3.5 w-3.5" /> Open in email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
